/**
 * Admin Products Page
 *
 * Lists every product in the catalog joined with retailer #1 (MSRP)
 * pricing. Plain admins see read-only; super admins can edit prices
 * inline, open the full edit modal, add/remove products, and manage
 * the product_colors lookup.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Package,
  Pencil,
  Plus,
  Search,
  Loader2,
  Save,
  ChevronUp,
  ChevronDown,
  Image as ImageIcon,
  StickyNote,
  AlertTriangle,
  Palette,
  Download,
  Upload,
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { AdminProductEditModal } from './AdminProductEditModal';
import { AdminImportModal } from './AdminImportModal';
import type { AdminProduct, AdminColor, AdminRetailerSummary } from '../../services/adminProductsApi';
import {
  fetchAdminProducts,
  patchAdminProduct,
  deleteAdminProduct,
  fetchAdminColors,
  fetchMasterExport,
} from '../../services/adminProductsApi';
import { downloadWorkbook, MASTER_COLUMNS } from '../../services/productSheet';

type SortField = 'sku' | 'name' | 'acronym' | 'category' | 'color' | 'lengthFeet' | 'msrpUsd' | 'msrpCad';
type SortDir = 'asc' | 'desc';

const PRODUCT_CATEGORIES = [
  'WallCeilingBoard',
  'SlatWall',
  'HTrim',
  'JTrimSnapIn',
  'JTrimHalf',
  'JTrimThreeQuarter',
  'BaseTrim',
  'InsideCoveTrim',
  'OutsideCorner',
  'SlatWallTopCap',
] as const;

interface PriceDraft {
  productId: number;
  field: 'msrpUsd' | 'msrpCad';
  value: string;
}

export const AdminProducts: React.FC = () => {
  const { isSuperAdmin } = useAdminAuth();
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [retailers, setRetailers] = useState<AdminRetailerSummary[]>([]);
  const [colors, setColors] = useState<AdminColor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortField>('acronym');
  const [order, setOrder] = useState<SortDir>('asc');
  const [editTargetId, setEditTargetId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [priceDraft, setPriceDraft] = useState<PriceDraft | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminProducts();
      setProducts(data.products);
      setRetailers(data.retailers);
      setColors(data.colors);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = products;
    if (q) {
      rows = rows.filter(p =>
        p.sku.toLowerCase().includes(q) ||
        p.productId.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.acronym.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.color.toLowerCase().includes(q),
      );
    }
    const dir = order === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort] ?? '';
      const bv = b[sort] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [products, search, sort, order]);

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ field, children, className }) => {
    const Icon = sort !== field ? ChevronDown : order === 'asc' ? ChevronUp : ChevronDown;
    return (
      <th className={`user-select-none ${className || ''}`} style={{ cursor: 'pointer' }} onClick={() => handleSort(field)}>
        <span className="d-inline-flex align-items-center gap-1">
          {children}
          <Icon size={12} className={sort === field ? '' : 'opacity-25'} />
        </span>
      </th>
    );
  };

  const colorSwatch = (slug: string): string => {
    const match = colors.find(c => c.slug === slug);
    return match?.hexCode ?? '#CCCCCC';
  };

  const startPriceEdit = (productId: number, field: 'msrpUsd' | 'msrpCad', current: number | null) => {
    if (!isSuperAdmin) return;
    setPriceDraft({ productId, field, value: current !== null ? current.toFixed(2) : '' });
  };

  const commitPriceEdit = async () => {
    if (!priceDraft) return;
    const product = products.find(p => p.id === priceDraft.productId);
    if (!product) {
      setPriceDraft(null);
      return;
    }
    const num = parseFloat(priceDraft.value);
    if (Number.isNaN(num) || num < 0) {
      setPriceDraft(null);
      return;
    }
    const original = product[priceDraft.field];
    if (original !== null && Math.abs(num - original) < 0.005) {
      setPriceDraft(null);
      return;
    }
    setSavingId(product.id);
    try {
      const next = await patchAdminProduct(product.id, {
        msrp: priceDraft.field === 'msrpUsd' ? { priceUsd: num } : { priceCad: num },
      });
      setProducts(prev => prev.map(p => (p.id === product.id
        ? { ...next.product, imageCount: p.imageCount, metaCount: p.metaCount }
        : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update price');
    } finally {
      setSavingId(null);
      setPriceDraft(null);
    }
  };

  const handleDelete = async (id: number, sku: string) => {
    setSavingId(id);
    try {
      await deleteAdminProduct(id, sku);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete product');
    } finally {
      setSavingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const data = await fetchMasterExport();
      const date = new Date().toISOString().slice(0, 10);
      downloadWorkbook(MASTER_COLUMNS, data as unknown as Record<string, unknown>[], `trusscore-products-${date}.xlsx`, 'Products');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const refreshColors = async () => {
    try {
      const next = await fetchAdminColors();
      setColors(next);
    } catch {
      /* surfaced via main error pipe on next load */
    }
  };

  return (
    <div>
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <div>
            <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
              <Package size={22} /> Products
            </h2>
            <p className="text-muted small mb-0">
              Master catalog with MSRP pricing (retailer&nbsp;#1).
              {!isSuperAdmin && <span className="ms-2 badge text-bg-secondary">Read-only</span>}
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
              onClick={handleExport}
              disabled={exporting}
              title="Download the catalog + MSRP pricing as an Excel file"
            >
              {exporting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />} Export
            </button>
            {isSuperAdmin && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                  onClick={() => setImportOpen(true)}
                  title="Import an edited Excel file"
                >
                  <Upload size={14} /> Import
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                  onClick={() => setCreateOpen(true)}
                >
                  <Plus size={14} /> Add Product
                </button>
              </>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="input-group input-group-sm" style={{ maxWidth: 360 }}>
            <span className="input-group-text"><Search size={14} /></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search SKU, name, category, color…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => setSearch('')}>Clear</button>
            )}
          </div>
          <span className="text-muted small ms-auto">{filtered.length} of {products.length} products</span>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-sm">
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover mb-0 align-middle">
            <thead className="table-tc-slate">
              <tr>
                <SortableHeader field="sku">SKU</SortableHeader>
                <SortableHeader field="name">Name</SortableHeader>
                <SortableHeader field="acronym">Type</SortableHeader>
                <SortableHeader field="category">Category</SortableHeader>
                <SortableHeader field="color">Color</SortableHeader>
                <SortableHeader field="lengthFeet" className="text-end">Length</SortableHeader>
                <th className="text-end">Sqft</th>
                <SortableHeader field="msrpUsd" className="text-end">MSRP USD</SortableHeader>
                <SortableHeader field="msrpCad" className="text-end">MSRP CAD</SortableHeader>
                <th className="text-center" title="Active">Active</th>
                <th className="text-center"><ImageIcon size={14} /></th>
                <th className="text-center"><StickyNote size={14} /></th>
                <th className="text-center" style={{ width: 60 }}>Edit</th>
              </tr>
            </thead>
            <tbody className="table-group-divider">
              {loading ? (
                <tr><td colSpan={13} className="text-center py-4 text-muted"><Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={13} className="text-center py-4 text-muted">No products match.</td></tr>
              ) : filtered.map(p => {
                const isEditingUsd = priceDraft?.productId === p.id && priceDraft.field === 'msrpUsd';
                const isEditingCad = priceDraft?.productId === p.id && priceDraft.field === 'msrpCad';
                const rowSaving = savingId === p.id;
                return (
                  <tr key={p.id} className={p.isActive ? '' : 'text-muted'}>
                    <td className="font-monospace small">{p.sku}</td>
                    <td>
                      <div className="small">{p.name}</div>
                      <div className="text-muted font-monospace" style={{ fontSize: '0.7em' }}>{p.productId}</div>
                    </td>
                    <td className="small">{p.acronym}</td>
                    <td className="small">{p.category}</td>
                    <td>
                      <span className="d-inline-flex align-items-center gap-1 small">
                        <span style={{ width: 12, height: 12, borderRadius: 3, background: colorSwatch(p.color), border: '1px solid #ccc', display: 'inline-block' }} />
                        {p.color}
                      </span>
                    </td>
                    <td className="text-end small">{p.lengthFeet ? `${p.lengthFeet}'` : '—'}</td>
                    <td className="text-end small">{p.sqft || '—'}</td>
                    <td
                      className="text-end small"
                      style={{ cursor: isSuperAdmin ? 'pointer' : 'default', minWidth: 90 }}
                      onClick={() => !isEditingUsd && startPriceEdit(p.id, 'msrpUsd', p.msrpUsd)}
                    >
                      {isEditingUsd ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm text-end"
                          autoFocus
                          value={priceDraft.value}
                          disabled={rowSaving}
                          onChange={e => setPriceDraft({ ...priceDraft, value: e.target.value })}
                          onBlur={commitPriceEdit}
                          onKeyDown={e => {
                            if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                            if (e.key === 'Escape') setPriceDraft(null);
                          }}
                        />
                      ) : (
                        p.msrpUsd !== null ? `$${p.msrpUsd.toFixed(2)}` : <span className="text-muted">—</span>
                      )}
                    </td>
                    <td
                      className="text-end small"
                      style={{ cursor: isSuperAdmin ? 'pointer' : 'default', minWidth: 90 }}
                      onClick={() => !isEditingCad && startPriceEdit(p.id, 'msrpCad', p.msrpCad)}
                    >
                      {isEditingCad ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm text-end"
                          autoFocus
                          value={priceDraft.value}
                          disabled={rowSaving}
                          onChange={e => setPriceDraft({ ...priceDraft, value: e.target.value })}
                          onBlur={commitPriceEdit}
                          onKeyDown={e => {
                            if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                            if (e.key === 'Escape') setPriceDraft(null);
                          }}
                        />
                      ) : (
                        p.msrpCad !== null ? `$${p.msrpCad.toFixed(2)}` : <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <span className={`badge ${p.isActive ? 'text-bg-success' : 'text-bg-secondary'}`}>{p.isActive ? 'Yes' : 'No'}</span>
                    </td>
                    <td className="text-center small">{p.imageCount || ''}</td>
                    <td className="text-center small">{p.metaCount || ''}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        title={isSuperAdmin ? 'Edit product' : 'View details (read-only)'}
                        onClick={() => setEditTargetId(p.id)}
                        disabled={rowSaving}
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ColorsPanel
        colors={colors}
        canEdit={isSuperAdmin}
        onChange={refreshColors}
      />

      {importOpen && (
        <AdminImportModal
          mode="master"
          onClose={() => setImportOpen(false)}
          onImported={refresh}
        />
      )}

      {(editTargetId !== null || createOpen) && (
        <AdminProductEditModal
          productId={editTargetId}
          isCreating={createOpen}
          retailers={retailers}
          colors={colors}
          categories={[...PRODUCT_CATEGORIES]}
          canEdit={isSuperAdmin}
          onClose={() => {
            setEditTargetId(null);
            setCreateOpen(false);
          }}
          onSaved={refresh}
          onDeleted={(id, sku) => {
            handleDelete(id, sku);
            setEditTargetId(null);
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// ColorsPanel — sub-section under the products table
// ============================================================================

interface ColorsPanelProps {
  colors: AdminColor[];
  canEdit: boolean;
  onChange: () => void;
}

const ColorsPanel: React.FC<ColorsPanelProps> = ({ colors, canEdit, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ slug: string; label: string; hexCode: string }>({
    slug: '',
    label: '',
    hexCode: '#CCCCCC',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<{ label: string; hexCode: string; isActive: boolean } | null>(null);

  const startEdit = (c: AdminColor) => {
    if (!canEdit) return;
    setEditingId(c.id);
    setEditDraft({ label: c.label, hexCode: c.hexCode, isActive: c.isActive });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = async (id: number) => {
    if (!editDraft) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/admin-colors.php?id=' + id, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editDraft),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Save failed');
      }
      cancelEdit();
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submitNew = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/admin-colors.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, sortOrder: (colors[colors.length - 1]?.sortOrder ?? 0) + 10, isActive: true }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Save failed');
      }
      setDraft({ slug: '', label: '', hexCode: '#CCCCCC' });
      setAdding(false);
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-3 shadow-sm p-3 mt-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
          <Palette size={16} /> Product Colors
        </h5>
        {canEdit && !adding && (
          <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => setAdding(true)}>
            <Plus size={12} /> Add Color
          </button>
        )}
      </div>
      <p className="text-muted small mb-2">
        Editable lookup fed into the product color dropdown. Slug is what gets stored on each product (e.g. <code>White</code>, <code>Gray</code>).
      </p>

      {error && <div className="alert alert-warning py-1 px-2 small mb-2">{error}</div>}

      <div className="d-flex flex-wrap gap-2">
        {colors.map(c => {
          const editing = editingId === c.id;
          return (
            <div key={c.id} className="border rounded-3 p-2 d-flex align-items-center gap-2" style={{ minWidth: 220, opacity: c.isActive ? 1 : 0.5 }}>
              {editing && editDraft ? (
                <>
                  <input
                    type="color"
                    value={editDraft.hexCode}
                    onChange={e => setEditDraft({ ...editDraft, hexCode: e.target.value.toUpperCase() })}
                    style={{ width: 32, height: 32, border: 'none', padding: 0, background: 'none' }}
                  />
                  <div className="d-flex flex-column flex-grow-1 gap-1">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={editDraft.label}
                      onChange={e => setEditDraft({ ...editDraft, label: e.target.value })}
                      placeholder="Label"
                    />
                    <div className="form-check form-switch m-0 small">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        role="switch"
                        checked={editDraft.isActive}
                        onChange={e => setEditDraft({ ...editDraft, isActive: e.target.checked })}
                      />
                      <label className="form-check-label small">Active</label>
                    </div>
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button type="button" className="btn btn-outline-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={() => saveEdit(c.id)} disabled={saving}>
                      {saving ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={12} />}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span style={{ width: 28, height: 28, borderRadius: 4, background: c.hexCode, border: '1px solid #ccc', display: 'inline-block' }} />
                  <div className="d-flex flex-column flex-grow-1">
                    <span className="fw-semibold small">{c.label}</span>
                    <span className="text-muted font-monospace" style={{ fontSize: '0.7em' }}>{c.slug} &middot; {c.hexCode}</span>
                  </div>
                  {!c.isActive && <span className="badge text-bg-secondary">Off</span>}
                  {canEdit && (
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => startEdit(c)}>
                      <Pencil size={12} />
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}

        {adding && (
          <div className="border rounded-3 p-2 d-flex align-items-center gap-2" style={{ minWidth: 280 }}>
            <input
              type="color"
              value={draft.hexCode}
              onChange={e => setDraft({ ...draft, hexCode: e.target.value.toUpperCase() })}
              style={{ width: 32, height: 32, border: 'none', padding: 0, background: 'none' }}
            />
            <div className="d-flex flex-column flex-grow-1 gap-1">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Slug (e.g. Beige)"
                value={draft.slug}
                onChange={e => setDraft({ ...draft, slug: e.target.value })}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Display label"
                value={draft.label}
                onChange={e => setDraft({ ...draft, label: e.target.value })}
              />
            </div>
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-outline-secondary" onClick={() => { setAdding(false); setDraft({ slug: '', label: '', hexCode: '#CCCCCC' }); }} disabled={saving}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={submitNew} disabled={saving || !draft.slug || !draft.label}>
                {saving ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={12} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
